export const poll = async <T = any>({
  fn,
  validate,
  interval,
  maxAttempts,
  maxErrorAttempts = 3,
  errorCodes = [400, 403, 404, 405, 500],
  signal,
}: {
  fn: () => Promise<T>;
  validate: (result: T, attempts: number, prevResult: T | null) => boolean;
  interval: number;
  maxAttempts?: number;
  maxErrorAttempts?: number;
  errorCodes?: number[];
  signal?: AbortSignal;
}): Promise<T> => {
  let attempts = 0;
  let errorAttempts = 0;
  let prevResult: T | null = null;
  let timeoutId: number | null = null;
  let isAbortSubscribed = false;

  const executePoll = async (resolve: (result: any) => void, reject: (error: any) => void) => {
    if (signal?.aborted) {
      return resolve(prevResult);
    }

    if (!isAbortSubscribed && signal) {
      isAbortSubscribed = true;
      signal.addEventListener(
        'abort',
        () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          resolve(prevResult);
        },
        { once: true },
      );
    }
    let result = prevResult;
    try {
      result = await fn();
    } catch (e) {
      if (signal?.aborted) {
        return;
      }

      const errorStatus = e.response?.status;
      if (errorStatus && errorCodes.includes(errorStatus)) {
        return reject(e);
      }

      errorAttempts += 1;
      if (maxErrorAttempts && errorAttempts === maxErrorAttempts) {
        return reject(e);
      }
    }

    if (signal?.aborted) {
      return;
    }

    attempts += 1;

    if (result && validate(result, attempts, prevResult)) {
      return resolve(result);
    }
    if (maxAttempts && attempts === maxAttempts) {
      return resolve(result);
    }
    prevResult = result;
    timeoutId = setTimeout(executePoll, interval, resolve, reject);
  };

  return new Promise(executePoll);
};
